/**
 * "Can't reach SaleFish" — the screen for a session check that got no answer.
 *
 * It exists because "could not ask" and "not signed in" are different facts.
 * The app used to treat a dropped connection during the session check as a
 * sign-out and send the user to the login page, where the next attempt spent
 * one of the office's rate-limited sign-ins and failed the same way. Now the
 * check is retried on its own (with backoff, and the moment the browser says it
 * is back online) and this says so, plainly, until it succeeds.
 *
 * Drawn by AuthProvider over the app's loader while
 * `useAuth().unreachable` is set; no route needs to know about it.
 */
import { useEffect, useState } from "react";
import { CloudOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SessionUnreachableState } from "@/contexts/AuthContext";
import { useTranslation } from "@/hooks/useTranslation";
import { localizeApiMessage } from "@/lib/apiError";

export function SessionUnreachable({
  state,
  onRetry,
}: {
  state: SessionUnreachableState;
  onRetry: () => void;
}) {
  const t = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.ceil((state.nextAttemptAt - now) / 1000));

  return (
    <div
      className="fixed inset-0 z-overlay flex items-center justify-center overflow-y-auto bg-background px-5 py-10"
      data-testid="session-unreachable"
    >
      <div className="w-full max-w-sm text-center">
        {/* The announced part. The countdown below sits OUTSIDE it: a live
            region re-announces every change, and a ticking clock inside one
            reads out once a second. */}
        <div role="alert">
        <span
          className="mx-auto flex size-12 items-center justify-center rounded-full bg-tint-neutral text-on-tint-neutral"
          aria-hidden="true"
        >
          <CloudOff className="size-6" />
        </span>
        <h1 className="mt-4 text-title font-bold">{t("shellErrorCantReachTitle", "Can’t reach SaleFish")}</h1>
        <p className="mt-2 text-body text-muted-foreground">{localizeApiMessage(state.message, t)}</p>
        <p className="mt-2 text-label font-semibold text-foreground">
          {state.wasSignedIn
            ? t("shellErrorStillSignedIn", "You're still signed in — this is a connection problem, not a sign-out.")
            : t("shellErrorNotSignedOut", "You haven't been signed out — this is a connection problem.")}
        </p>
        </div>
        <Button className="mt-5 w-full sm:w-auto" onClick={onRetry} disabled={state.retrying} data-testid="button-retry-session">
          <RefreshCw className={state.retrying ? "animate-spin" : undefined} />
          {state.retrying ? t("shellErrorRetrying", "Trying Again…") : t("shellErrorTryAgain", "Try Again")}
        </Button>
        <p className="mt-3 text-caption text-muted-foreground" aria-hidden="true">
          {state.retrying
            ? t("shellErrorCheckingConnection", "Checking the Connection…")
            : seconds > 0
              ? t("shellErrorRetryCountdown", "Trying again automatically in {seconds}s.", { seconds })
              : t("shellErrorRetryingAutomatically", "Trying Again Automatically…")}
        </p>
        <p className="sr-only">{t("shellErrorKeepTrying", "SaleFish will keep trying on its own.")}</p>
      </div>
    </div>
  );
}
